import React, {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Link as DSLink,
  Loader,
  Timeline,
  type TimelineColorThemesKinds,
  type TimelineConnectorKinds,
  type TimelineMedallionKinds,
  Typography,
} from '@saviynt/design-system';
import { format, isValid } from 'date-fns';

import { AUDIT_TRAIL_MAP } from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import { useLazyGetSummaryDetailsQuery } from '../../store/globalApis';
import { type SodSidePanelSummaryData } from '../../types';
import {
  createDevRejectionHandler,
  extractKeyValuePairs,
  isDate,
} from '../../utils';
import SodActVsPotChip from '../SodActVsPotChip';
import SodEndPointChip from '../SodEndPointChip';
import SodFieldSet from '../SodFieldSet';
import SodFunction from '../SodFunction';
import SodRiskPriorityChip from '../SodRiskPriorityChip';
import SodStatusChip from '../SodStatusChip';
import SodUserCard from '../SodUserCard';

import './SodSidePanelSummary.css';

const INVERSE_TABS_MAPPING: Record<string, string> = {
  OPEN: 'openCount',
  RISK_ACCEPTED: 'mitigatedCount',
  IN_PROGRESS: 'inReviewCount',
  REMEDIATED: 'remediatedCount',
  CLOSED: 'closedCount',
} as const;

type TypeLevel = 'SOD' | 'CRITICAL_ACCESS' | 'NONE';

const RISK_TYPE_MAP = {
  SOD: { text: 'SOD' },
  CRITICAL_ACCESS: { text: 'Critical Access' },
  NONE: { text: 'None' },
} satisfies Record<TypeLevel, { text?: string }>;

interface SodSidePanelSummaryProps {
  sodKey: string;
  isInSidePanel?: boolean;
  isClosedTab?: boolean;
  className: string;
  leadingChildren?: ReactNode | null;
  trailingChildren?: ReactNode | null;
  handleOnUserClick: (value: string) => void;
  handleOnViewHistoryClick?: () => void | null;
}

/**
 * SodSidePanelSummary
 */
function SodSidePanelSummary({
  sodKey,
  handleOnUserClick,
  handleOnViewHistoryClick,
  isInSidePanel = false,
  isClosedTab = false,
  className,
  leadingChildren,
  trailingChildren,
}: SodSidePanelSummaryProps): ReactElement | null {
  const l = useInvertedLocalize();
  const { globalDateFormat } = useTypedSelector((state) => state.aag.global);

  const [data, setData] = useState<SodSidePanelSummaryData | null>(null);
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchSummary, { isLoading, isError, isFetching }] =
    useLazyGetSummaryDetailsQuery();

  const handlefetchSummary = useCallback(
    async (payload: string) => {
      abortRef.current?.('New Search');

      try {
        const controller = fetchSummary(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed Summary API');
      }
    },
    [fetchSummary]
  );

  const triggerFetchSummary = useCallback(
    (key: string) => {
      handlefetchSummary(key)
        .then((response) => {
          const typedResponse = response as SodSidePanelSummaryData;

          setData(typedResponse);
        })
        .catch(
          createDevRejectionHandler('SodSidePanelSummary: handlefetchSummary')
        );
    },
    [handlefetchSummary]
  );

  useEffect(() => {
    triggerFetchSummary(sodKey);
  }, [sodKey, triggerFetchSummary]);

  if (isLoading || isFetching) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>
          {l('Error loading summary details, please try again.')}
        </Typography>
        <Button
          type='button'
          kind='outlined'
          isEnabled
          onClick={() => {
            triggerFetchSummary(sodKey);
          }}>
          {l('Retry')}
        </Button>
      </Box>
    );
  }

  if (data === null) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>{l('No data found!')}</Typography>
      </Box>
    );
  }

  const {
    actualVsPotential,
    status,
    risk,
    user,
    accounts,
    endpoints,
    violationOrigin = '-',
    businessProcess = '-',
    riskOwner = '-',
  } = data.violationData;

  const accountNames = accounts
    .map((account) => account.accountName)
    .join(' · ');
  const endpointsArray = endpoints.map((endpoint, index) => (
    <SodEndPointChip
      key={endpoint.endpointName}
      endpoints={[
        {
          endpointName: endpoint.endpointName,
          endpointLogo: endpoint.endpointLogo,
        },
      ]}
      isPrefixInterpoint={index !== endpoints.length - 1}
    />
  ));

  const violationOriginValue = violationOrigin
    ? `${
        violationOrigin.charAt(0).toUpperCase() +
        violationOrigin.slice(1).toLowerCase()
      } Analysis`
    : '-';

  const getAvailableFunction = () => {
    const potentialFunctions = [
      risk.function1,
      risk.function2,
      risk.function3,
      risk.function4,
      risk.function5,
    ];

    const availableFunctions = potentialFunctions
      .filter((func) => func?.functionKey && func.functionName)
      .map((func) => ({
        id: func?.functionKey,
        text: func?.functionName,
      }));

    return availableFunctions;
  };

  interface LatestActiviytTimelineBlockData {
    title: string;
    attributes: { label: string; detail: string }[];
    connectorKind: TimelineConnectorKinds;
    medallionKind: TimelineMedallionKinds;
    icon: string;
    colorTheme: TimelineColorThemesKinds;
    date: Date;
    dateFormat?: string;
    timeFormat?: string;
    isTimeEnabled: boolean;
    size: 'small' | 'medium';
  }

  const getLatestActiviy = () => {
    if (data.latestActivity !== null) {
      const { auditEventType, changeLog, eventTimestamp } = data.latestActivity;

      // Type guard to check if the key exists in the map
      const eventType = auditEventType as keyof typeof AUDIT_TRAIL_MAP;
      const latestActivityBlock = AUDIT_TRAIL_MAP[eventType];
      const timelineSize = 'small' as const;

      const latestActivityData: LatestActiviytTimelineBlockData[] = [
        {
          title: l(latestActivityBlock.text),
          attributes: Array.from(
            new Map(
              extractKeyValuePairs(changeLog)
                .filter((logItem) => Boolean(logItem.value))
                .map((logItem) => ({
                  label: l(logItem.key),
                  detail:
                    isDate(logItem.key) &&
                    isValid(new Date(String(logItem.value)))
                      ? format(
                          new Date(String(logItem.value)),
                          globalDateFormat
                        )
                      : logItem.value ?? '',
                }))
                .map((item) => [`${item.label}-${item.detail}`, item])
            ).values()
          ),
          connectorKind:
            latestActivityBlock.connectorKind as TimelineConnectorKinds,
          medallionKind:
            latestActivityBlock.medallionKind as TimelineMedallionKinds,
          icon: latestActivityBlock.icon,
          colorTheme:
            latestActivityBlock.colorTheme as TimelineColorThemesKinds,
          date: new Date(eventTimestamp),
          isTimeEnabled: false,
          size: timelineSize,
        },
      ];

      return (
        <Box tag='div' className='Sod-SidePanelSummary-latestActivity'>
          <Typography kind='h4'>{l('Latest Activity')}</Typography>
          <Timeline timelineBlocksData={latestActivityData} />
          <DSLink
            text={l('View History')}
            color='brand'
            kind='button'
            onClick={() => handleOnViewHistoryClick?.()}
            size='medium'
            className='Sod-SidePanelSummary-latestActivity-links'
          />
          <span className='Sod-SidePanelSummary-conflictingFnsDivider' />
        </Box>
      );
    }

    return null;
  };

  const availableFunctions = getAvailableFunction();

  const getInitialFourFieldSets = () => (
    <Box className='Sod-SidePanelSummary-InitialFieldSets'>
      <SodFieldSet
        label={l('Violation Status')}
        value={
          <SodStatusChip
            kind='xSmall'
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            tabInAction={INVERSE_TABS_MAPPING[status] ?? ''}
            isDashReturn
          />
        }
      />
      <SodFieldSet
        label={l('Risk Score')}
        value={
          <SodRiskPriorityChip
            priority={risk.riskSeverity ?? risk.riskPriority}
            riskScore={Number(risk.riskScore)}
            isPrefixInterpoint={false}
            isDashReturn
          />
        }
      />
      <SodFieldSet
        label={l('Violation Age')}
        value={l('{age} days', { age: String(risk.violationAge) })}
      />
      <SodFieldSet
        label={l('Actual vs Potential')}
        value={
          <SodActVsPotChip
            value={actualVsPotential}
            isPrefixInterpoint={false}
          />
        }
      />
    </Box>
  );

  return (
    <Box className={className}>
      {leadingChildren}
      {/* Below are the temp solutions */}
      {isInSidePanel && !isClosedTab && availableFunctions.length > 0 ? (
        <Box tag='div' className='Sod-SidePanelSummary-conflictingFns'>
          <SodFieldSet
            label={l('Conflicting Job Functions')}
            value={<SodFunction functions={availableFunctions} />}
          />
          {trailingChildren}
          <span className='Sod-SidePanelSummary-conflictingFnsDivider' />
        </Box>
      ) : null}
      {isInSidePanel && getLatestActiviy()}
      {getInitialFourFieldSets()}
      <SodFieldSet
        label={l('User')}
        value={
          user.username ? (
            <SodUserCard
              firstName={user.firstName}
              lastName={user.lastName}
              userName={user.username}
              link=''
              trailContent={
                <DSLink
                  text={user.displayName ? user.displayName : user.username}
                  kind='button'
                  onClick={() => handleOnUserClick(String(user.displayName))}
                  className='Sod-ViolationsSidePanelBody-links'
                />
              }
            />
          ) : (
            '-'
          )
        }
      />
      {endpointsArray.length > 0 && (
        <SodFieldSet
          label={l('Application')}
          value={
            <Box tag='div' className='Sod-SidePanelSummary-endpointWrapper'>
              {endpointsArray}
            </Box>
          }
        />
      )}
      {accountNames.trim().length > 0 && (
        <SodFieldSet label={l('Accounts')} value={accountNames} />
      )}
      <SodFieldSet label={l('Violation Origin')} value={violationOriginValue} />
      {risk.riskDescription !== null &&
        risk.riskDescription.trim().length > 0 && (
          <SodFieldSet
            label={l('Risk Description')}
            value={risk.riskDescription}
          />
        )}
      {risk.ruleset && (
        <SodFieldSet label={l('Ruleset')} value={risk.ruleset} />
      )}
      <SodFieldSet
        label={l('Business Process')}
        value={businessProcess || '-'}
      />
      {(risk.riskSeverity || risk.riskPriority) && (
        <SodFieldSet
          label={l('Risk Priority')}
          value={
            <SodRiskPriorityChip
              priority={risk.riskPriority}
              riskScore={Number(risk.riskScore)}
              isPrefixInterpoint={false}
              isOnlyLabel
              isDashReturn
              isMediumRiskAsModerate={false}
            />
          }
        />
      )}
      {risk.riskType && (
        <SodFieldSet
          label={l('Risk Type')}
          value={l(
            (risk.riskType as TypeLevel) in RISK_TYPE_MAP
              ? RISK_TYPE_MAP[risk.riskType as TypeLevel].text
              : risk.riskType
          )}
        />
      )}
      <SodFieldSet label={l('Risk Owner')} value={riskOwner || '-'} />
    </Box>
  );
}

SodSidePanelSummary.defaultProps = {
  isInSidePanel: false,
  isClosedTab: false,
  leadingChildren: null,
  trailingChildren: null,
  handleOnViewHistoryClick: null,
};

export default memo(SodSidePanelSummary);
